import React from "react";
import { Box, Stack, Button, FormControl, Divider, Paper } from "@mui/material";
import GroupSelector from "../AddAssignment/GroupSelector";
import DeadlineSelector from "../components/DeadlineSelector";
import NotificationSettings from "../components/NotificationSettings";
import { AssignmentFormValues } from "../types";
import { SelectedUser } from "../types";
import { brand } from "../../../theme/themePrimitives";
import { GroupOption } from "../hooks/usePortalGroups";
import { AssignmentActionMode } from "./AssignmentActionToggle";

interface AssignmentFormLayoutProps {
  mode: AssignmentActionMode;
  selectedItems: SelectedUser[];
  itemType: "users" | "assignments";
  formValues: AssignmentFormValues;
  onViewUsers?: (users: SelectedUser[] | undefined) => void;
  onFormValuesChange: (values: Partial<AssignmentFormValues>) => void;
  onSubmit: () => void;
  submitButtonText: string;
  extraContent?: React.ReactNode;
  badge?: string;
  isSubmitting?: boolean;
  openDeadlinePicker?: boolean;
  onOpenDeadlinePickerChange?: () => void;
  onCloseDeadlinePickerChange?: () => void;
  groupsData?: {
    groups: GroupOption[];
    loading: boolean;
    error: string | null;
  };
}

const AssignmentFormLayout: React.FC<AssignmentFormLayoutProps> = ({
  mode,
  selectedItems,
  formValues,
  itemType,
  onFormValuesChange,
  onSubmit,
  submitButtonText,
  extraContent,
  isSubmitting = false,
  openDeadlinePicker = false,
  onOpenDeadlinePickerChange,
  onCloseDeadlinePickerChange,
  groupsData,
}) => {
  return (
    <Box>
      {extraContent}

      <Paper
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          border: "1px solid #EAECF0",
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
          mt: 3,
          padding: 3,
        }}
      >
        <Box>
          <Stack spacing={3}>
            <Box>
              <FormControl fullWidth>
                <GroupSelector
                  value={formValues.group}
                  onChange={(v) => onFormValuesChange({ group: v })}
                  groupsData={groupsData}
                />
              </FormControl>
            </Box>

            <Divider />

            <Box>
              <DeadlineSelector
                deadlineDate={formValues.deadlineDate}
                deadlineOffset={formValues.deadlineOffset}
                setDeadlineToNull={formValues.setDeadlineToNull}
                openDeadlinePicker={openDeadlinePicker}
                onOpenDeadlinePickerChange={onOpenDeadlinePickerChange}
                onCloseDeadlinePickerChange={onCloseDeadlinePickerChange}
                onDateChange={(date) => {
                  onFormValuesChange({ deadlineDate: date, notify: true });
                }}
                onOffsetChange={(offset) =>
                  onFormValuesChange({ deadlineOffset: offset })
                }
                onSetDeadlineToNullChange={(checked) => {
                  if (checked) {
                    onFormValuesChange({
                      setDeadlineToNull: checked,
                      deadlineDate: "",
                      deadlineOffset: "",
                    });
                  } else {
                    onFormValuesChange({
                      setDeadlineToNull: checked,
                    });
                  }
                }}
              />
            </Box>

            {/* Notification Settings Section */}
            {mode === ("create" as AssignmentActionMode) && (
              <Box>
                <NotificationSettings
                  notify={formValues.notify}
                  disabled={!!formValues.deadlineDate}
                  onNotifyChange={(checked) =>
                    onFormValuesChange({ notify: checked })
                  }
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 6 }}>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting}
          sx={{
            backgroundColor: `${brand[600]} !important`,
            color: `white`,
            textTransform: "none",
            borderRadius: "12px",
            px: 2,
            py: 1.25,
            height: "44px",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            "&:disabled": {
              bgcolor: "#9CA3AF",
              color: "white",
            },
            "&:hover": {
              backgroundColor: `${brand[700]} !important`,
            },
          }}
        >
          {isSubmitting ? "Submitting..." : submitButtonText}
        </Button>
      </Box>
    </Box>
  );
};

export default AssignmentFormLayout;
